<template>
	<!-- LSP-20787 (D18): list = the compact pill whose tooltip is the next step, opened by tap on touch
	     (LSP-15907 recipe, SettingsHelpTooltip.vue); detail = the full sentence, no tooltip. -->
	<v-tooltip
		v-if="display && size === 'list'"
		location="bottom"
		:disabled="false"
		:open-on-hover="!DeviceUtility.isTouch"
		:open-on-click="DeviceUtility.isTouch">
		<template #activator="{ props: tooltipProps }">
			<!-- A real <button>: button semantics are what make tap-to-open fire on iOS/Android (LSP-15907).
			     @click.stop.prevent keeps the table row (ConversationsPage.vue:99) and the card link (ConversationCard.vue) from navigating. -->
			<button
				v-bind="tooltipProps"
				type="button"
				:class="['sms-delivery-chip', 'sms-delivery-chip--list', `sms-delivery--${display.tone}`]"
				:aria-label="`${display.label}. ${tooltipText}`"
				:data-state="state"
				:data-tone="display.tone"
				data-test-id="sms-delivery-chip"
				@click.stop.prevent>
				<span v-if="!toneIcon" class="sms-delivery__dot" aria-hidden="true" />
				<v-icon v-else size="12" class="sms-delivery__glyph" aria-hidden="true">{{ toneIcon }}</v-icon>
				<span class="sms-delivery__label">{{ display.label }}</span>
				<v-icon size="12" aria-hidden="true" data-test-id="sms-delivery-channel">{{ channelIcon }}</v-icon>
			</button>
		</template>
		<span>{{ tooltipText }}</span>
	</v-tooltip>
	<span
		v-else-if="display"
		:class="['sms-delivery-chip', 'sms-delivery-chip--detail', `sms-delivery--${display.tone}`]"
		:data-state="state"
		:data-tone="display.tone"
		data-test-id="sms-delivery-chip">
		<span v-if="!toneIcon" class="sms-delivery__dot" aria-hidden="true" />
		<v-icon v-else size="14" class="sms-delivery__glyph" aria-hidden="true">{{ toneIcon }}</v-icon>
		<span>{{ sentenceText }}</span>
	</span>
</template>

<script setup lang="ts">
	import { computed, watch } from "vue";
	import type { SmsDeliveryState } from "@/api/index.d";
	import DeviceUtility from "@/helpers/device";
	import { SMS_DELIVERY_DISPLAY, SMS_DELIVERY_TONE_ICONS, formatDeliverySince, type SmsDeliveryDisplay } from "@/helpers/smsDelivery";
	import "@/styles/sms-delivery.scss";

	const props = defineProps<{ state: SmsDeliveryState; since: string; size: "list" | "detail" }>();

	// B8: a state the API adds before this table learns it renders nothing (and warns once), never a render error.
	const display = computed<SmsDeliveryDisplay | undefined>(() => SMS_DELIVERY_DISPLAY[props.state]);
	const toneIcon = computed(() => (display.value ? SMS_DELIVERY_TONE_ICONS[display.value.tone] : null));
	const channelIcon = computed(() => (display.value?.channel === "email" ? "mdi-email-outline" : "mdi-message-text-outline"));
	const tooltipText = computed(() => display.value?.tooltip.replace("{time}", formatDeliverySince(props.since)) ?? "");
	const sentenceText = computed(() => display.value?.sentence.replace("{time}", formatDeliverySince(props.since)) ?? "");
	watch(
		() => props.state,
		(state) => {
			if (!SMS_DELIVERY_DISPLAY[state]) console.warn("SmsDeliveryChip: unknown delivery state, rendering nothing", { state });
		},
		{ immediate: true }
	);
</script>

<style scoped lang="scss">
	/* Geometry from the prototype (D18); colours come from the shared tone classes (src/styles/sms-delivery.scss). */
	.sms-delivery-chip {
		display: inline-flex;
		align-items: center;
		border-radius: 9999px;
		font-weight: 600;
		line-height: 1.25;
	}
	.sms-delivery-chip--list {
		gap: 5px;
		padding: 3px 9px;
		font-family: inherit;
		font-size: 11.5px;
		white-space: nowrap;
		cursor: default;
		/* LSP-20787 fix round 1: the list pill can sit in a narrow footer half (ConversationCard.vue,
		   ConversationsPage.vue's status column) that isn't always wide enough for the full label.
		   Capped here + min-width:0 so it shrinks instead of forcing its ancestors wider; the label
		   below is what actually gives up space (ellipsis) — the tooltip already carries the full
		   next-step text, so truncating the label loses nothing. Scoped to --list only: the header's
		   sentence chip (--detail) is untouched. */
		max-width: 100%;
		min-width: 0;
	}
	.sms-delivery-chip--detail {
		gap: 7px;
		padding: 6px 14px;
		font-size: 13px;
	}

	.sms-delivery-chip--list .sms-delivery__label {
		overflow: hidden;
		text-overflow: ellipsis;
		min-width: 0;
	}
</style>
